import {Button} from "@zendeskgarden/react-buttons";
import {Field, Hint, Label, Toggle} from "@zendeskgarden/react-forms";
import {Skeleton} from "@zendeskgarden/react-loaders";
import {ReactComponent as PlusIcon} from "@zendeskgarden/svg-icons/src/16/plus-stroke.svg";
import {Fragment, ReactElement, useState} from "react";
import {toast} from "react-toastify";
import {Property} from "../../../contexts/properties/PropertiesContext";
import NarrativeSectionEditor from "./components/NarrativeSectionEditor";
import NarrativeSectionRow from "./components/NarrativeSectionRow";
import RemoveSectionModal from "./components/RemoveSectionModal";
import UnsavedChangesModal from "./components/UnsavedChangesModal";
import useExecutiveSummaryNarrative, {NarrativeSection} from "./logic/useExecutiveSummaryNarrative";
import * as css from "./styles/narrativeEditor.module.scss";


const NEW_SECTION_ID = "new";

interface NarrativeEditorProps {
    property: Property;
    onDone: () => void;
}

export default function NarrativeEditor(props: NarrativeEditorProps): ReactElement {
    const narrative = useExecutiveSummaryNarrative(props.property);

    const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
    const [draftTitle, setDraftTitle] = useState("");
    const [draftBody, setDraftBody] = useState("");
    const [titleError, setTitleError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const [sectionToRemove, setSectionToRemove] = useState<NarrativeSection | null>(null);
    const [isRemoving, setIsRemoving] = useState(false);
    const [removeError, setRemoveError] = useState<string | null>(null);

    const [actionAfterUnsavedChanges, setActionAfterUnsavedChanges] = useState<(() => void) | null>(null);

    const editingSection = narrative.sections.find(section => section.id === editingSectionId);
    let savedTitle = "New Section";
    let savedBody = "";
    if (editingSection !== undefined) {
        savedTitle = editingSection.title;
        savedBody = editingSection.body;
    }
    const isDirty = editingSectionId !== null && (draftTitle !== savedTitle || draftBody !== savedBody);

    function openSectionEditor(sectionId: string, title: string, body: string) {
        setEditingSectionId(sectionId);
        setDraftTitle(title);
        setDraftBody(body);
        setTitleError(null);
    }

    function runAfterCheckingUnsavedChanges(action: () => void) {
        if (isDirty) {
            setActionAfterUnsavedChanges(() => action);
        } else {
            action();
        }
    }

    async function handleSave(): Promise<boolean> {
        if (draftTitle.trim() === "") {
            setTitleError("A title is required.");
            return false;
        }

        setTitleError(null);
        setIsSaving(true);

        let isSaved = false;
        if (editingSectionId === NEW_SECTION_ID) {
            isSaved = await narrative.createSection(draftTitle, draftBody);
        } else if (editingSectionId !== null) {
            isSaved = await narrative.updateSection(editingSectionId, draftTitle, draftBody);
        }

        setIsSaving(false);

        if (!isSaved) {
            toast.error("The section could not be saved. Please try again.");
            return false;
        }

        setEditingSectionId(null);
        return true;
    }

    async function handleSaveFromUnsavedChangesModal() {
        const action = actionAfterUnsavedChanges;
        const isSaved = await handleSave();

        setActionAfterUnsavedChanges(null);

        if (isSaved && action) {
            action();
        }
    }

    function handleDiscardFromUnsavedChangesModal() {
        setEditingSectionId(null);
        setActionAfterUnsavedChanges(null);
        actionAfterUnsavedChanges?.();
    }

    async function handleMove(sectionId: string, previousSectionId: string | null) {
        const isSaved = await narrative.moveSection(sectionId, previousSectionId);
        if (!isSaved) {
            toast.error("The new section order could not be saved. Please try again.");
        }
    }

    async function handleUseCustomNarrativeChange(useCustomNarrative: boolean) {
        const isSaved = await narrative.setUseCustomNarrative(useCustomNarrative);
        if (!isSaved) {
            toast.error("There was a problem saving, please refresh and try again.");
        } else {
            toast.success(useCustomNarrative ? "Using custom summary" : "Using default summary");
        }
    }

    async function handleConfirmRemove() {
        if (!sectionToRemove) {
            return;
        }

        setIsRemoving(true);
        setRemoveError(null);

        const isDeleted = await narrative.deleteSection(sectionToRemove.id);

        setIsRemoving(false);

        if (isDeleted) {
            setSectionToRemove(null);
        } else {
            setRemoveError("The section could not be removed. Please try again.");
        }
    }

    const sectionEditor = (
        <NarrativeSectionEditor
            title={draftTitle}
            body={draftBody}
            titleError={titleError}
            isSaving={isSaving}
            onTitleChange={setDraftTitle}
            onBodyChange={setDraftBody}
            onCancel={() => runAfterCheckingUnsavedChanges(() => setEditingSectionId(null))}
            onSave={handleSave}
        />
    );

    let cardContent: ReactElement;

    if (narrative.isLoading) {
        cardContent = (
            <div className={css.loading}>
                <Skeleton height="25px" />
                <Skeleton height="25px" />
                <Skeleton height="25px" width="60%" />
            </div>
        );
    } else {
        cardContent = (
            <>
                {narrative.sections.length === 0 && editingSectionId !== NEW_SECTION_ID &&
                    <p className={css.emptyState}>No sections yet. Vizibly's default summary will continue to be shown.</p>
                }

                {narrative.sections.map((section, index) => {
                    if (section.id === editingSectionId) {
                        return <Fragment key={section.id}>{sectionEditor}</Fragment>;
                    }

                    return (
                        <NarrativeSectionRow
                            key={section.id}
                            section={section}
                            isFirst={index === 0}
                            isLast={index === narrative.sections.length - 1}
                            isMuted={editingSectionId !== null}
                            onMoveUp={() => handleMove(section.id, narrative.sections[index - 2]?.id ?? null)}
                            onMoveDown={() => handleMove(section.id, narrative.sections[index + 1]?.id ?? null)}
                            onEdit={() => openSectionEditor(section.id, section.title, section.body)}
                            onRemove={() => {
                                setRemoveError(null);
                                setSectionToRemove(section);
                            }}
                        />
                    );
                })}

                {editingSectionId === NEW_SECTION_ID &&
                    sectionEditor
                }

                <div className={css.addRow}>
                    <Button
                        onClick={() => openSectionEditor(NEW_SECTION_ID, "New Section", "")}
                        disabled={editingSectionId !== null}
                    >
                        <Button.StartIcon><PlusIcon /></Button.StartIcon>
                        Add a Section
                    </Button>
                </div>
            </>
        );
    }

    return (
        <div className={css.page}>
            <div className={css.pageHeader}>
                <div className={css.pageHeaderContent}>
                    <div className={css.pageHeaderText}>
                        <h1 className={css.pageTitle}>Editing Executive Summary</h1>
                        <p className={css.pageDescription}>Compose your own custom summary by writing and arranging sections.</p>
                    </div>
                    <div className={css.pageHeaderActions}>
                        <Button isPrimary onClick={() => runAfterCheckingUnsavedChanges(props.onDone)}>
                            Done Editing
                        </Button>
                    </div>
                </div>
            </div>

            <div className={css.pageBody}>
                <div className={css.bodyGrid}>
                    <div className={css.card}>
                        <div className={css.cardHeader}>
                            <div>
                                <h2 className={css.cardTitle}>Sections</h2>
                                <p className={css.cardHelper}>Hover a section to edit, reorder or remove it. Sections print in the order you see them.</p>
                            </div>

                            <Field>
                                <Toggle
                                    checked={narrative.useCustomNarrative}
                                    disabled={narrative.isLoading}
                                    onChange={evt => handleUseCustomNarrativeChange(evt.target.checked)}
                                >
                                    <Label>Use Custom Summary</Label>
                                    {narrative.useCustomNarrative ?
                                        <Hint>Disable to use the default summary</Hint>
                                        :
                                        <Hint>Enable to use your custom summary</Hint>
                                    }
                                </Toggle>
                            </Field>
                        </div>
                        {cardContent}
                    </div>
                </div>
            </div>

            {sectionToRemove &&
                <RemoveSectionModal
                    sectionTitle={sectionToRemove.title}
                    isRemoving={isRemoving}
                    errorMessage={removeError}
                    onCancel={() => setSectionToRemove(null)}
                    onConfirm={handleConfirmRemove}
                />
            }

            {actionAfterUnsavedChanges &&
                <UnsavedChangesModal
                    isSaving={isSaving}
                    onKeepEditing={() => setActionAfterUnsavedChanges(null)}
                    onDiscard={handleDiscardFromUnsavedChangesModal}
                    onSave={handleSaveFromUnsavedChangesModal}
                />
            }
        </div>
    );
}
